// THE WORDS AND SHAPES OF THE TODAY PAGE, kept out of the component.
//
// The server hands back facts (src/actions/briefing.js); this file turns them into
// what the mockup draws — design/today/Today.dc.html, Gabriel's redesign of
// 2026-09-30. Pure and ESM so test/today-model.test.js loads the same file the
// bundle does.

import { BOARD_STAGE_LABELS, moneyK } from "./ui.js";
import { isHot } from "./hot.mjs";

// Sections that are not queue work. Meetings are fixtures and live on the day
// strip; a call with no invite is shown against the day it was meant to be on.
export const NOT_QUEUE = new Set(["meetings", "invites"]);

/** The ids of the queue's companies that are hot, by the board's own rule. */
export function hotIds(inputs, now = new Date()) {
  return new Set((inputs || []).filter((co) => isHot(co, { now })).map((co) => co.id));
}

/*
 * THE KINDS WHERE THEY ARE WAITING ON HIM: they wrote (a reply, an answer on the
 * agreement) or a form asked for a call and the first email is owed. Mirrors
 * AWAITING_HIM in src/actions/index.js, which keeps a hot one on the page past the
 * queue's shelf life.
 */
export const AWAITING_HIM = new Set(["reply_waiting", "agreement_reply", "first_touch"]);

/**
 * THE QUEUE, HOT LEADS FIRST. Every row for a hot company is lifted out of its own
 * group into one group at the top, in the server's section order, so the leads
 * actually in play are the first thing read (Gabriel, 2026-10-08). The rest keep
 * their groups underneath. Each row's `group` says which group it is drawn in, so
 * a sent row's receipt lands in the same place.
 *
 * Of those, a hot lead WAITING ON HIM goes higher still: its own group, drawn above
 * everything else on the page (Gabriel, 2026-10-15). A row a call today answers is
 * not waiting on an email, so it stays with the other hot rows.
 */
export function arrangeQueue(groups, hot) {
  const waiting = [];
  const lifted = [];
  const must = groups.map((g) => {
    const rows = [];
    for (const a of g.rows) {
      if (!hot.has(a.company_id)) rows.push({ ...a, group: g.key });
      else if (AWAITING_HIM.has(a.kind) && !answeredByCall(a)) waiting.push({ ...a, group: "waiting", hot: true });
      else lifted.push({ ...a, group: "hot", hot: true });
    }
    return { ...g, rows };
  });
  return {
    waiting: { key: "waiting", title: "Hot leads waiting on you", rows: waiting },
    hot: { key: "hot", title: "Hot leads", rows: lifted },
    must,
  };
}

/*
 * A ROW A CALL TODAY ANSWERS. The server marks it (call_today_at, CALL_ANSWERS in
 * src/actions/index.js): he is speaking to them today, so the row stays where it is
 * but owes nothing, reads when the call is, and offers the record to prep from
 * instead of a composer.
 */
export const answeredByCall = (a) => !!a.call_today_at;

/** Rows still owed, which is what every count on the page counts. */
export function owed(rows) {
  return (rows || []).filter((a) => !answeredByCall(a));
}

/** "Speaking at 11:30", or "Spoke at 11:30" once it has started. */
export function callTodayLine(a, now = new Date()) {
  const at = new Date(a.call_today_at);
  return `${at > now ? "Speaking" : "Spoke"} at ${hhmm(at)} today`;
}

/*
 * THE REST OF HIS CALENDAR on the day strip: grey blocks for time that is taken by
 * anything that is not a lead call (src/calendarView.js busyBlocks). An event that
 * is already a call card is not drawn twice.
 */
export function busyFor(busy, calls) {
  const ids = new Set((calls || []).map((c) => String(c.event_id)));
  return (busy || []).filter((b) => !ids.has(String(b.id)));
}

/** Busy blocks overlapping each day, like bucketCalls but by overlap, not start. */
export function bucketBusy(busy, now = new Date()) {
  const t0 = dayStart(now).getTime();
  const t1 = t0 + MS_DAY;
  const t2 = t1 + MS_DAY;
  const today = [];
  const tomorrow = [];
  for (const b of busy || []) {
    const s = new Date(b.start).getTime();
    const e = new Date(b.end).getTime();
    if (s < t1 && e > t0) today.push(b);
    if (s < t2 && e > t1) tomorrow.push(b);
  }
  return { today, tomorrow };
}

/** Lanes for busy blocks, by their real length: two overlapping events stack. */
export function laneBusy(blocks) {
  const ends = [];
  return [...(blocks || [])]
    .sort((a, b) => new Date(a.start) - new Date(b.start))
    .map((b) => {
      const s = new Date(b.start).getTime();
      let lane = ends.findIndex((e) => e <= s);
      if (lane === -1) {
        lane = ends.length;
        ends.push(0);
      }
      ends[lane] = new Date(b.end).getTime();
      return { block: b, lane };
    });
}

// The grey line under each group heading, from the mockup. Keyed by the section
// keys in src/actions/model.js TODAY_SECTIONS; a section without one shows none.
export const SECTION_HINTS = {
  waiting: "In play and waiting on your answer. Before anything else",
  hot: "In play right now. Start here",
  replies: "They wrote, you haven’t answered",
  newleads: "Form came in, first email owed",
  kickoffs: "Signed, welcome email owed",
  slackchannels: "Signed, no internal channel with you and Lukasz",
  financeintros: "Signed, billing handover owed",
  owed: "You promised them something",
  callactions: "What you said on the call you’d send or do",
  callfollowups: "Call held, follow-up owed",
  nudges: "Went quiet, time to chase",
  chasing: "Pricing or agreement out, no answer",
};

// The short label under each segment of the weight bar.
export const SECTION_SHORT = {
  waiting: "Hot, waiting",
  hot: "Hot leads",
  replies: "Replies",
  newleads: "New leads",
  kickoffs: "Welcomes",
  slackchannels: "Slack channels",
  financeintros: "Finance intros",
  owed: "Owed",
  callactions: "Action items",
  callfollowups: "Follow-ups",
  stages: "Stage updates",
  nudges: "Nudges",
  chasing: "Quiet too long",
  wakeups: "Wake-ups",
};

// "Replied to Dana at Helix Omics" — the receipt a row turns into once it is sent.
const DONE_VERBS = {
  reply_waiting: "Replied to",
  agreement_reply: "Replied to",
  first_touch: "Wrote to",
  kickoff: "Welcomed",
  finance_intro: "Introduced finance to",
  call_followup: "Followed up with",
  nudge_due: "Nudged",
  final_check_due: "Checked in with",
  pricing_stale: "Chased",
  agreement_stale: "Chased",
};

export function doneText(action) {
  // Not an email: the receipt says the list is done, not who was written to.
  if (action.kind === "call_actions") return `Action items done for ${action.company_name || action.company_domain || "them"}`;
  const first = (action.contact_name || "").split(" ")[0];
  const verb = DONE_VERBS[action.kind] || "Emailed";
  const co = action.company_name || action.company_domain || "";
  return first ? `${verb} ${first} at ${co}` : `${verb} ${co}`;
}

// How long a row has waited: "today", "1d", "16d". A wake-up is a date, not an age.
export function waitLabel(action) {
  if (action.kind === "wake_up_due" && action.at) return shortDate(action.at);
  const d = action.age_days;
  if (d === null || d === undefined) return "";
  return d === 0 ? "today" : `${d}d`;
}

export const MS_DAY = 86400000;

function dayStart(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function localMidnight(now = new Date()) {
  return dayStart(now);
}

export function hhmm(ts) {
  return new Date(ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}

export function shortDate(ts) {
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Calls split into the page's today and tomorrow, by the browser's own clock. */
export function bucketCalls(calls, now = new Date()) {
  const t0 = dayStart(now).getTime();
  const t1 = t0 + MS_DAY;
  const t2 = t1 + MS_DAY;
  const today = [];
  const tomorrow = [];
  for (const c of calls || []) {
    const at = new Date(c.at).getTime();
    if (at >= t0 && at < t1) today.push(c);
    else if (at >= t1 && at < t2) tomorrow.push(c);
  }
  return { today, tomorrow };
}

/**
 * The strip's hour range: 08:00–19:00 as in the mockup, widened to fit any call
 * outside it. DataHub's weekly is at 22:30 Israel time, and a strip that clipped
 * it would say the evening is free.
 */
export function stripRange(calls) {
  let start = 8;
  let end = 19;
  for (const c of calls || []) {
    const s = new Date(c.at);
    const h = s.getHours() + s.getMinutes() / 60;
    const e = c.end_at ? new Date(c.end_at) : new Date(s.getTime() + 30 * 60000);
    const eh = e.getHours() + e.getMinutes() / 60 + (e.getDate() !== s.getDate() ? 24 : 0);
    start = Math.min(start, Math.floor(h));
    end = Math.max(end, Math.ceil(eh) + 1);
  }
  return { start, end: Math.min(end, 24) };
}

export function hourOf(ts) {
  const d = new Date(ts);
  return d.getHours() + d.getMinutes() / 60;
}

/**
 * Stack calls into lanes so two cards never sit on top of each other. A card is a
 * fixed width in pixels, so whether two calls collide depends on how wide the
 * strip is: `span` is how many hours one card covers at the current width.
 */
export function laneCalls(calls, span) {
  const ends = []; // per lane, the hour its last card reaches
  return (calls || []).map((c) => {
    const h = hourOf(c.at);
    let lane = ends.findIndex((e) => e <= h);
    if (lane === -1) {
      lane = ends.length;
      ends.push(0);
    }
    ends[lane] = h + span;
    return { call: c, lane };
  });
}

/** "Rachel Ong, VP Marketing · Call scheduled". A client says so instead of a stage. */
export function callWho(c) {
  const person = [c.contact_name, c.contact_job_title].filter(Boolean).join(", ");
  const where = c.status === "client" ? "Client" : BOARD_STAGE_LABELS[c.pipeline_stage] || null;
  return [person, where].filter(Boolean).join(" · ");
}

/** The first call today that has not started yet: the one the strip marks yellow. */
export function nextCall(todayCalls, now = new Date()) {
  return (todayCalls || []).find((c) => new Date(c.at) > now) || null;
}

/** "Next in 1h 18m" for the phone's day header. */
export function untilLabel(ts, now = new Date()) {
  const mins = Math.round((new Date(ts) - now) / 60000);
  if (mins <= 0) return "now";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `in ${h}h ${m}m` : `in ${m}m`;
}

export function greeting(now = new Date()) {
  const h = now.getHours();
  return h < 12 ? "Morning" : h < 18 ? "Afternoon" : "Evening";
}

/** The line under the progress count. */
export function progressLine({ done, remaining, first }) {
  if (remaining === 0) return done ? "Every item handled. Go get a second coffee." : "";
  if (done === 0) {
    if (!first) return "";
    const who = (first.contact_name || "").split(" ")[0];
    const wait = waitLabel(first);
    return who && wait && wait !== "today"
      ? `Start with ${first.company_name}. ${who} has been waiting ${wait}.`
      : `Start with ${first.company_name}.`;
  }
  const ratio = done / (done + remaining);
  return ratio >= 0.5 ? `Past halfway. ${remaining} to go.` : `Good start. Next up: ${first ? first.company_name : "the next one"}.`;
}

// "+3", "−1", "" — a real minus sign, so the column of deltas lines up.
export function delta(now, then) {
  const d = (now || 0) - (then || 0);
  return d > 0 ? `+${d}` : d < 0 ? `−${-d}` : "";
}

/** Rows for the pipeline card: every open stage, then won, with bar widths. */
export function pipelineRows(pipeline) {
  if (!pipeline) return [];
  const rows = pipeline.stages.map((s) => ({
    key: s.stage,
    name: BOARD_STAGE_LABELS[s.stage] || s.stage,
    count: s.count,
    delta: delta(s.count, s.then),
    won: false,
  }));
  rows.push({ key: "won", name: "Won, 30 days", count: pipeline.won.count, delta: delta(pipeline.won.count, pipeline.won.then), won: true });
  const max = Math.max(1, ...rows.map((r) => r.count));
  return rows.map((r) => ({ ...r, pct: (r.count / max) * 100 }));
}

/** Today / Yesterday / This week, by the browser's clock. */
export function feedGroups(feed, now = new Date()) {
  const t0 = dayStart(now).getTime();
  const groups = [
    { label: "Today", items: [] },
    { label: "Yesterday", items: [] },
    { label: "This week", items: [] },
  ];
  for (const e of feed || []) {
    const at = new Date(e.at).getTime();
    const g = at >= t0 ? 0 : at >= t0 - MS_DAY ? 1 : 2;
    groups[g].items.push(e);
  }
  return groups.filter((g) => g.items.length);
}

// The time column: a clock for the last two days, a weekday or date before that.
export function feedTime(ts, now = new Date()) {
  const at = new Date(ts);
  if (at.getTime() >= dayStart(now).getTime() - MS_DAY) return hhmm(at);
  const days = (dayStart(now) - dayStart(at)) / MS_DAY;
  return days < 7 ? at.toLocaleDateString("en-US", { weekday: "short" }) : shortDate(at);
}

function quoteOf(text, n = 70) {
  if (!text) return null;
  const one = String(text).replace(/\s+/g, " ").trim();
  if (!one) return null;
  return `“${one.length > n ? one.slice(0, n - 1).trimEnd() + "…" : one}”`;
}

// The status_reason vocabulary the board's recycle and disqualify menus write
// (BoardPage.js), plus the ghost sweep's. Anything else says nothing rather than
// showing a raw key.
const REASONS = {
  ghosted: "Went quiet",
  no_budget: "No budget",
  no_budget_now: "No budget now",
  timing: "Timing",
  browsing: "Browsing",
  bad_fit: "Bad fit",
  misaligned: "Misaligned",
};

/** What one feed event says: a title, a smaller line, and which mark it gets. */
export function feedLine(e) {
  const co = e.company_name || "A company";
  const stage = (s) => BOARD_STAGE_LABELS[s] || s;
  switch (e.kind) {
    case "won": {
      const fee = e.monthly_value ? `${moneyK(e.monthly_value, e.currency)}/mo retainer` : null;
      const terms = e.signed_fee_structure ? String(e.signed_fee_structure).split("\n")[0] : null;
      return { title: co, sub: fee || terms || "Signed", mark: "win" };
    }
    case "lost":
      return { title: `${co} closed lost`, sub: REASONS[e.status_reason] || null, mark: "lost" };
    case "parked":
      return {
        title: e.recycle_until ? `${co} parked until ${shortDate(e.recycle_until)}` : `${co} parked`,
        sub: REASONS[e.status_reason] || null,
        mark: "parked",
      };
    case "returning":
      return {
        title: `${co} is back in the pipeline`,
        sub: [e.to_stage ? `at ${stage(e.to_stage)}` : null, e.from_value === "churned" ? "Former client" : null].filter(Boolean).join(" · ") || null,
        mark: "moved",
      };
    case "new_lead":
      return { title: `New lead: ${co}`, sub: null, mark: "form" };
    case "moved":
      return {
        title: `${co} moved to ${stage(e.to_value)}`,
        sub: e.from_value ? `from ${stage(e.from_value)}` : null,
        mark: e.to_value === "proposal_sent" ? "pricing" : "moved",
      };
    case "form":
      return { title: `New form from ${co}`, sub: [e.contact_name, quoteOf(e.text)].filter(Boolean).join(" · ") || null, mark: "form" };
    case "reply":
      return { title: `${co} replied`, sub: [e.contact_name, e.text].filter(Boolean).join(" · ") || null, mark: "reply" };
    case "call":
      // Held only when Fireflies recorded it. A calendar event in the past says the
      // call was booked, and a booked call can still be a no-show.
      return { title: `${e.type === "call" ? "Call held" : "Call"} with ${co}`, sub: e.contact_name || e.text || null, mark: "call" };
    case "room_step":
      // A step they ticked on their room (migration 073).
      return { title: `${co} ticked “${e.text}”`, sub: e.contact_name ? `${e.contact_name}, on their room` : "On their room", mark: "ready" };
    case "room_ready":
      // The room's "let's move forward" (migration 070): his cue to send the paper.
      return { title: `${co} wants to move forward`, sub: "Pressed the next-step button on their room", mark: "ready" };
    case "room":
      // Their prospect room (src/rooms): "Acme opened their room (3rd time) — looked at Pricing".
      return {
        title: `${e.person_name ? `${e.person_name} (${co})` : co} opened ${e.person_name ? "the" : "their"} room (${ordinalWord(e.nth || 1)} time)${e.top_section ? ` — looked at ${ROOM_SECTION[e.top_section] || e.top_section}` : ""}`,
        sub: roomSub(e),
        mark: "room",
      };
    default:
      return { title: co, sub: null, mark: "moved" };
  }
}

const ROOM_SECTION = {
  decks: "Decks & examples",
  pricing: "Pricing",
  recaps: "Call recaps",
  agreement: "Agreement",
  conversation: "Our conversation",
};

function ordinalWord(n) {
  const v = n % 100;
  const suffix = v >= 11 && v <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th";
  return `${n}${suffix}`;
}

function roomSeconds(s) {
  const n = Math.round(Number(s) || 0);
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  const r = n % 60;
  return r >= 5 ? `${m}m ${r}s` : `${m}m`;
}

// "4m 10s on Pricing · 2 people today, 1 of them new". People are browsers, never names.
function roomSub(e) {
  const parts = [];
  if (e.top_section && e.top_seconds) parts.push(`${roomSeconds(e.top_seconds)} on ${ROOM_SECTION[e.top_section] || e.top_section}`);
  const n = Number(e.people_today) || 0;
  if (n > 1) parts.push(`${n} people today${e.new_person ? ", 1 of them new" : ""}`);
  else if (n === 1) parts.push(e.new_person && (e.nth || 1) > 1 ? "1 person, new to the room" : "1 person");
  return parts.join(" · ") || null;
}

// The glyph in each feed mark. Monochrome, per COMPONENTS.md: colour belongs to
// pills and tags, not icon chips — the win card is the one green thing.
export const MARK_GLYPHS = {
  form: "+",
  reply: "↩",
  call: "☏",
  pricing: "$",
  moved: "→",
  lost: "×",
  parked: "",
  room: "◫",
  ready: "✓",
};


/*
 * ONE LINE PER LEAD. The feed used to give every event its own row, so a lead that
 * wrote, had a call and moved stage took three lines (Gabriel, 2026-10-08: "a single
 * line for each lead"). Events are folded per company over the whole window; the line
 * sits under the day of its latest event and is ordered by it.
 */
export function feedByLead(feed) {
  const by = new Map();
  for (const e of feed || []) {
    const key = e.company_id || `${e.company_name}|${e.at}`;
    if (!by.has(key)) by.set(key, { company_id: e.company_id, company_name: e.company_name, at: e.at, events: [] });
    const lead = by.get(key);
    lead.events.push(e);
    if (new Date(e.at) > new Date(lead.at)) lead.at = e.at;
  }
  for (const lead of by.values()) {
    lead.events = foldEvents(lead.events.sort((a, b) => new Date(a.at) - new Date(b.at)));
    if (lead.events.length) lead.at = lead.events[lead.events.length - 1].at;
  }
  return [...by.values()].filter((l) => l.events.length).sort((a, b) => new Date(b.at) - new Date(a.at));
}

const MIN = 60000;
const near = (a, b, ms) => Math.abs(new Date(a.at) - new Date(b.at)) <= ms;

/*
 * WHAT ACTUALLY CHANGED over the week, not every write that got it there.
 *
 *   - A park undone within a day is a correction, not news: Schlafender Hase was
 *     parked by the ghost sweep and put back by hand 30 seconds later. Both go.
 *   - A return and the stage it returned to are written in the same instant
 *     (Sygnia): one step, "Back in the pipeline at Call scheduled".
 *   - Stage moves with nothing between them collapse into where they ended up, and
 *     vanish if that is where they started.
 */
export function foldEvents(events) {
  let list = [...(events || [])];

  const undone = new Set();
  list.forEach((e, i) => {
    if (e.kind !== "parked" || undone.has(i)) return;
    const j = list.findIndex((x, k) => k > i && !undone.has(k) && x.kind === "returning" && x.from_value === "recycled" && near(e, x, MS_DAY));
    if (j === -1) return;
    undone.add(i).add(j);
    // The stage put back with it is the stage it already had.
    const k = list.findIndex((x, n) => !undone.has(n) && x.kind === "moved" && !x.from_value && near(list[j], x, MIN));
    if (k !== -1) undone.add(k);
  });
  list = list.filter((_, i) => !undone.has(i));

  const merged = [];
  for (const e of list) {
    const prev = merged[merged.length - 1];
    if (prev && prev.kind === "returning" && !prev.to_stage && e.kind === "moved" && !e.from_value && near(prev, e, MIN)) {
      merged[merged.length - 1] = { ...prev, to_stage: e.to_value, at: e.at };
    } else if (prev && e.kind === "returning" && prev.kind === "moved" && !prev.from_value && near(prev, e, MIN)) {
      merged[merged.length - 1] = { ...e, to_stage: prev.to_value };
    } else {
      merged.push(e);
    }
  }

  const out = [];
  for (const e of merged) {
    const prev = out[out.length - 1];
    if (prev && prev.kind === "moved" && e.kind === "moved") {
      out[out.length - 1] = { ...e, from_value: prev.from_value };
    } else {
      out.push(e);
    }
  }
  return out.filter((e) => !(e.kind === "moved" && e.from_value && e.from_value === e.to_value));
}

// Which event's mark a folded line wears: the one that matters most, not the latest.
const MARK_RANK = ["win", "lost", "parked", "ready", "pricing", "moved", "call", "reply", "room", "form"];

const FEED_STEPS = 4;

// A step on a folded line, in a few words.
function stepOf(e) {
  const stage = (s) => BOARD_STAGE_LABELS[s] || s;
  switch (e.kind) {
    case "won": return "Won";
    case "lost": return "Closed lost";
    case "parked": return e.recycle_until ? `Parked until ${shortDate(e.recycle_until)}` : "Parked";
    case "returning": return e.to_stage ? `Back in the pipeline at ${stage(e.to_stage)}` : "Back in the pipeline";
    case "new_lead": return "New lead";
    case "moved": return `Moved to ${stage(e.to_value)}`;
    case "form": return "Form";
    case "reply": return "Replied";
    case "call": return e.type === "call" ? "Call held" : "Call";
    case "room": return `${e.person_name ? `${e.person_name} opened` : "Opened"} the room${e.top_section ? ` (${ROOM_SECTION[e.top_section] || e.top_section})` : ""}`;
    case "room_step":
      // A step they ticked on their room (migration 073).
      return { title: `${co} ticked “${e.text}”`, sub: e.contact_name ? `${e.contact_name}, on their room` : "On their room", mark: "ready" };
    case "room_ready": return "Ready to move forward";
    case "room_step": return `Ticked “${e.text}”`;
    default: return null;
  }
}

/** What one lead's line says. One event reads as before; several are listed in order. */
export function feedLeadLine(lead) {
  if (lead.events.length === 1) return feedLine(lead.events[0]);
  const lines = lead.events.map(feedLine);
  const mark = MARK_RANK.find((m) => lines.some((l) => l.mark === m)) || "moved";
  if (mark === "win") return { ...lines.find((l) => l.mark === "win"), mark };
  const steps = [];
  for (const e of lead.events) {
    const step = stepOf(e);
    if (!step) continue;
    const last = steps[steps.length - 1];
    if (last && last.text === step) last.n++;
    else steps.push({ text: step, n: 1 });
  }
  // A busy week can run to six steps; the line keeps the latest four.
  const words = steps.map((x) => (x.n > 1 ? `${x.text} ×${x.n}` : x.text));
  const shown = words.length > FEED_STEPS ? ["…", ...words.slice(-FEED_STEPS)] : words;
  return {
    title: lead.company_name || "A company",
    sub: shown.join(" → ") || null,
    mark,
  };
}

/** The mail behind a lead's "Replied" steps, for the feed's Not a reply. */
export function replyIds(lead) {
  return (lead.events || []).filter((e) => e.kind === "reply").flatMap((e) => e.activity_ids || []);
}
